import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import Archive from '@/assets/icons/archive.svg?react';
import Train from '@/assets/icons/train.svg?react';
import PillTabs from '@/components/ui/pill-tabs';
import useRoleAccess from '@/hooks/use-role-access';
import { APP_ROUTES } from '@/lib/routes/app-routes';
import { OPS_SUB_ROLES, USER_ROLES } from '@/utils/constants/roles';

type BottomNavProps = {
  onSwitchRole: (role: string, onSettled?: () => void) => void;
  isRoleSwitching: boolean;
};

const BottomNav = ({ onSwitchRole, isRoleSwitching }: BottomNavProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { isAd, isAdAndQs, selectedPermissionRole } = useRoleAccess();

  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const activeTab = selectedTab ?? pathname;

  const handleTabChange = useCallback(
    (tabRoute: string) => {
      if (isAd && !isAdAndQs) {
        navigate(tabRoute);

        return;
      }

      setSelectedTab(tabRoute);

      if (tabRoute === APP_ROUTES.dashboard) {
        // Users who picked a context at login return to that one, not to whichever
        // role their held roles would otherwise imply.
        const contextRole =
          selectedPermissionRole === USER_ROLES.ops ? OPS_SUB_ROLES[0].value : USER_ROLES.ad;
        const impliedRole = isAdAndQs ? USER_ROLES.ad : OPS_SUB_ROLES[0].value;
        const dashboardRole = selectedPermissionRole ? contextRole : impliedRole;

        onSwitchRole(dashboardRole, () => setSelectedTab(null));
      } else {
        onSwitchRole(USER_ROLES.qs, () => setSelectedTab(null));
      }

      navigate(tabRoute);
    },
    [onSwitchRole, navigate, isAd, isAdAndQs, selectedPermissionRole]
  );

  const tabData = useMemo(
    () => [
      {
        key: APP_ROUTES.dashboard,
        label: <Train className="size-5" aria-label={t('common:dashboard.title')} />,
      },
      {
        key: APP_ROUTES.archive,
        label: <Archive className="size-5" aria-label={t('common:archive.dashboard.title')} />,
      },
    ],
    [t]
  );

  return (
    <div className="bg-chat-background border-train-stroke flex h-15 w-full shrink-0 items-center border-t">
      <PillTabs
        tabs={tabData}
        value={activeTab}
        onChange={handleTabChange}
        disabled={isRoleSwitching}
      />
    </div>
  );
};

export default BottomNav;
